import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  ModalBodyContent,
  ModalButtonsContainer,
  ModalCloseButtonV2,
  StyledModalBody,
  StyledModalTitle,
  StyledModalV2
} from '../../../../components/Modals';
import { Flex } from '../../../../components/ui/Flex';
import { closeActiveModal } from '../../../../helper/modals';
import styled, { css } from 'styled-components';
import { Fonts, Palette } from '../../../../components/utils';
import { OptionText, TitleText } from '../../PaymentComponents';
import { ButtonBodyVariant } from '../../../../components/ui/ButtonVariant';
import { setToast } from '../../../../redux/actions/window';
import {
  axiosErrorMessageForToast,
  buildRentReceiptRequestBody,
  capitalizePaymentStatusWords,
  downloadVopayReceiptPdf,
  fetchVopayReceiptPreview,
  formatReceiptPaymentMethodDisplay,
  paymentListStatusBadgeMeta
} from '../../PaymentUtils';
import { PaymentStatusBadge } from '../../PaymentStatusBadge.jsx';
import ModalCloseIcon from '../../../../assets/icons/modal-close-button.svg';

const PAYMENT_DETAILS_SECTION_DIVIDER = '#e0e0e0';

const receiptModalBodyCss = css`
  &.modal-body {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
`;

const PaymentReceiptSideModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { modal, data } = useSelector((state) => state.headerModal);
  const isOpen = modal === 'PaymentReceiptSideModal';
  const [previewData, setPreviewData] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const requestBody = useMemo(() => buildRentReceiptRequestBody(data), [data]);

  useEffect(() => {
    if (!isOpen) {
      setPreviewData(null);
      setIsPreviewLoading(false);
      setIsDownloading(false);
      return undefined;
    }

    if (!requestBody) {
      dispatch(
        setToast({
          toastShow: true,
          toastMessage: t('Could not load receipt: missing payment identifiers'),
          modal: 'errorToast'
        })
      );
      return undefined;
    }

    let cancelled = false;
    setIsPreviewLoading(true);
    fetchVopayReceiptPreview(requestBody)
      .then((res) => {
        if (cancelled) return;
        setPreviewData(res && typeof res === 'object' ? res : null);
      })
      .catch(async (err) => {
        if (cancelled) return;
        const msg = await axiosErrorMessageForToast(err, t('Unknown error'));
        dispatch(
          setToast({
            toastShow: true,
            toastMessage: t('Could not load receipt: {{error}}', { error: msg }),
            modal: 'errorToast'
          })
        );
      })
      .finally(() => {
        if (cancelled) return;
        setIsPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, isOpen, requestBody, t]);

  const onDownload = () => {
    if (!requestBody || isDownloading) return;
    setIsDownloading(true);
    downloadVopayReceiptPdf(requestBody)
      .catch(async (err) => {
        const errMsg = await axiosErrorMessageForToast(err, t('Unknown error'));
        dispatch(
          setToast({
            toastShow: true,
            toastMessage: t('Could not download receipt: {{error}}', { error: errMsg }),
            modal: 'errorToast'
          })
        );
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  const receiptStatus = String(previewData?.paymentStatus || '').trim();
  const statusMeta = paymentListStatusBadgeMeta(previewData?.paymentStatus ?? receiptStatus);
  const capitalizedPaymentStatus = capitalizePaymentStatusWords(receiptStatus);
  const methodDisplay = formatReceiptPaymentMethodDisplay(
    previewData?.paymentMethodType,
    previewData?.paymentMethodLast4
  );
  const tenantDisplay = Array.isArray(previewData?.tenants) ? previewData.tenants.filter(Boolean).join('\n') : '';
  const addressStreetLine = String(previewData?.buildingAddress ?? '').trim();
  const addressCityStateZipLine = [previewData?.buildingCity, previewData?.buildingState, previewData?.buildingZip]
    .filter((part) => String(part ?? '').trim() !== '')
    .join(', ');
  const unitStr =
    previewData?.unitNum != null && String(previewData.unitNum) !== '' ? String(previewData.unitNum) : '';
  const addressStreetWithUnit =
    unitStr && addressStreetLine
      ? `Unit ${unitStr} - ${addressStreetLine}`
      : unitStr
        ? `Unit ${unitStr}`
        : addressStreetLine;
  const amountDisplay = useMemo(() => {
    const raw = previewData?.amount;
    if (raw == null || String(raw).trim() === '') return '—';
    const n = Number(raw);
    return Number.isFinite(n) ? `$${n.toFixed(2)}` : '—';
  }, [previewData?.amount]);

  return (
    <StyledModalV2
      show={isOpen}
      onClose={closeActiveModal}
      closeOnBackdrop={false}
      width={'550px'}
      side
    >
      <StyledModalBody cssOverride={receiptModalBodyCss}>
        <ReceiptHeader className="modal-header">
          <ReceiptHeaderTop>
            <ReceiptTitle>{t('Receipt')}</ReceiptTitle>
            <ReceiptHeaderActions>
              {receiptStatus ? (
                <PaymentStatusBadge
                  status={previewData?.paymentStatus ?? receiptStatus}
                  t={t}
                  label={statusMeta ? undefined : capitalizedPaymentStatus}
                />
              ) : null}
              <ModalCloseButtonV2 type="button" onClick={closeActiveModal} aria-label={t('Close')}>
                <img src={ModalCloseIcon} alt="close" />
              </ModalCloseButtonV2>
            </ReceiptHeaderActions>
          </ReceiptHeaderTop>
        </ReceiptHeader>
        <ModalBodyContent>
          <Flex direction="column" gap="1.25rem">
            {isPreviewLoading ? (
              <OptionText cssOverride="text-transform: none;">{t('Loading receipt...')}</OptionText>
            ) : (
              <SectionContainer>
                <SectionTitle>{t('Resident')}</SectionTitle>
                <SectionRows>
                  <DetailRow>
                    <FieldLabel>{t('Tenant')}</FieldLabel>
                    <FieldValueMulti>{tenantDisplay || '—'}</FieldValueMulti>
                  </DetailRow>
                  <DetailRow>
                    <FieldLabel>{t('Address')}</FieldLabel>
                    {addressStreetWithUnit || addressCityStateZipLine ? (
                      <Flex direction="column" gap="0.25rem" alignItems="flex-start" style={{ flex: '1 1 auto' }}>
                        {addressStreetWithUnit ? <FieldValue>{addressStreetWithUnit}</FieldValue> : null}
                        {addressCityStateZipLine ? <FieldValue>{addressCityStateZipLine}</FieldValue> : null}
                      </Flex>
                    ) : (
                      <FieldValue>—</FieldValue>
                    )}
                  </DetailRow>
                </SectionRows>
              </SectionContainer>
            )}

            <ModalSectionDivider />

            {isPreviewLoading ? null : (
              <SectionContainer>
                <SectionTitle>{t('Payment Details')}</SectionTitle>
                <SectionRows>
                  <DetailRow>
                    <FieldLabel>{t('Receipt Number')}</FieldLabel>
                    <FieldValue>{previewData?.receiptNumber || '—'}</FieldValue>
                  </DetailRow>
                  <DetailRow>
                    <FieldLabel>{t('Receipt Date')}</FieldLabel>
                    <FieldValue>{previewData?.paymentDate || '—'}</FieldValue>
                  </DetailRow>
                  <DetailRow>
                    <FieldLabel>{t('Amount')}</FieldLabel>
                    <FieldValue>{amountDisplay}</FieldValue>
                  </DetailRow>
                  <DetailRow>
                    <FieldLabel>{t('Payment Purpose')}</FieldLabel>
                    <FieldValue>{previewData?.paymentPurpose || '—'}</FieldValue>
                  </DetailRow>
                  <DetailRow>
                    <FieldLabel>{t('Payment Period')}</FieldLabel>
                    <FieldValue>{previewData?.paymentPeriod || '—'}</FieldValue>
                  </DetailRow>
                  <DetailRow>
                    <FieldLabel>{t('Payment Method')}</FieldLabel>
                    <FieldValue>{methodDisplay}</FieldValue>
                  </DetailRow>
                  <DetailRow>
                    <FieldLabel>{t('Payment Status')}</FieldLabel>
                    <FieldValue>
                      {statusMeta ? t(statusMeta.label) : capitalizedPaymentStatus}
                    </FieldValue>
                  </DetailRow>
                </SectionRows>
              </SectionContainer>
            )}
          </Flex>
        </ModalBodyContent>
      </StyledModalBody>

      <ModalButtonsContainer gap="0.5rem" isAtBottom>
        <FooterActions>
          <ButtonBodyVariant variant="secondary" cssOverride="min-width: 120px;" onClick={closeActiveModal}>
            {t('Back')}
          </ButtonBodyVariant>
          <ButtonBodyVariant
            type="button"
            cssOverride="min-width: 120px;"
            onClick={onDownload}
            disabled={isPreviewLoading || isDownloading || !requestBody}
          >
            {isDownloading ? t('Downloading...') : t('Download')}
          </ButtonBodyVariant>
        </FooterActions>
      </ModalButtonsContainer>
    </StyledModalV2>
  );
};

const ReceiptHeader = styled.div`
  &.modal-header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
  }

  &&.modal-header {
    padding-right: 0 !important;
    padding-bottom: 24px !important;
    border-bottom: none !important;
  }
`;

const ReceiptHeaderTop = styled.div`
  align-self: stretch;
  box-sizing: border-box;
  padding: 0 0 20px 0;
  border-bottom: 1px solid ${PAYMENT_DETAILS_SECTION_DIVIDER};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const ReceiptHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ReceiptTitle = styled(StyledModalTitle).attrs({ as: 'div' })`
  margin: 0;
`;

const ModalSectionDivider = styled.hr.attrs({ 'aria-hidden': true })`
  display: block;
  width: 100%;
  height: 1px;
  margin: 1.25rem 0;
  border: 0;
  background: ${PAYMENT_DETAILS_SECTION_DIVIDER};
  flex-shrink: 0;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SectionTitle = styled(TitleText)`
  font-size: 1.25rem;
  line-height: 2rem;
`;

const SectionRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const FieldLabel = styled.p`
  color: ${Palette.HAZY_BLUE};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 600;
  line-height: 1.25rem;
  letter-spacing: 0rem;
  margin: 0;
  flex: 0 0 45%;
`;

const FieldValue = styled.p`
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.25rem;
  letter-spacing: 0rem;
  margin: 0;
  flex: 1 1 auto;
  text-align: left;
  text-transform: none;
`;

const FieldValueMulti = styled(FieldValue)`
  white-space: pre-line;
`;

const FooterActions = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  position: relative;
  padding-top: 1rem;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -24px;
    right: -24px;
    border-top: 1px solid ${Palette.BORDER_OUTLINE};
  }
`;

export default PaymentReceiptSideModal;
